import Moment from 'moment';
import { SET_QUOTA_EXCEEDED_INFO } from '../../../constants/actionTypes';
import { isApiSuccess } from '../../../constants/platformStatusCodes';

// ─── Status helpers ───────────────────────────────────────────────────────────

export const AUDIENCE_STATUS = {
  INITIATED: 'Initiated',
  PROCESSING: 'Processing',
  SEGMENTS_READY: 'SegmentsReady',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
};

export const isProcessingStatus = (status) =>
  [AUDIENCE_STATUS.INITIATED, AUDIENCE_STATUS.PROCESSING].includes(status);

export const isCompletedStatus = (status) =>
  [AUDIENCE_STATUS.SEGMENTS_READY, AUDIENCE_STATUS.COMPLETED].includes(status);

export const NON_TERMINAL_AUDIENCE_STATUSES = [
  AUDIENCE_STATUS.INITIATED,
  AUDIENCE_STATUS.PROCESSING,
  AUDIENCE_STATUS.SEGMENTS_READY,
];

// 3-state influencer-generation contract, shared by the segment cards
// (segments[].influencersStatus) and the influencer list (data.status) —
// FE_INFLUENCER_GENERATION_STATUS_GUIDE §1.
export const SEGMENT_INFLUENCERS_STATUS = {
  GENERATING: 'generating',
  READY: 'ready',
  UNAVAILABLE: 'unavailable',
};

const SEGMENT_INFLUENCERS_STATUS_VALUES = new Set(Object.values(SEGMENT_INFLUENCERS_STATUS));

/**
 * Validates a raw `influencersStatus` value against the 3-state contract. Absence and unknown
 * values both map to null, and null must render exactly like before the field existed (no
 * spinner, no polling) — backwards- and forward-compatible by construction.
 */
export function normalizeInfluencersStatus(value) {
  const v = typeof value === 'string' ? value.toLowerCase() : null;
  return SEGMENT_INFLUENCERS_STATUS_VALUES.has(v) ? v : null;
}

// ─── URL helpers ──────────────────────────────────────────────────────────────

export function ensureSubjectUrl(url) {
  const trimmed = url?.trim() ?? '';
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

// ─── Date helpers ─────────────────────────────────────────────────────────────

export function formatDate(isoString) {
  if (!isoString) return null;
  const m = Moment(isoString);
  if (!m.isValid()) return null;
  return m.format('D MMM, YYYY');
}

// ─── Tag builders ─────────────────────────────────────────────────────────────

function buildTags(record) {
  const raw = record.demographics;
  const demo = Array.isArray(raw) || !raw ? {} : raw;
  const flat = (v) => (Array.isArray(v) ? v.join(', ') : v) || null;

  const tags = [];
  if (record.subjectType) tags.push({ key: 'type', value: record.subjectType });
  if (flat(demo.income)) tags.push({ key: 'income', value: flat(demo.income) });
  if (flat(demo.language)) tags.push({ key: 'language', value: flat(demo.language) });
  if (flat(demo.location)) tags.push({ key: 'location', value: flat(demo.location) });
  if (flat(demo.gender)) tags.push({ key: 'gender', value: flat(demo.gender) });
  if (flat(demo.ethnicity)) tags.push({ key: 'ethnicity', value: flat(demo.ethnicity) });
  if (flat(demo.education)) tags.push({ key: 'education', value: flat(demo.education) });
  if (flat(demo.age)) tags.push({ key: 'age', value: flat(demo.age) });
  return tags;
}

/** @deprecated Use buildSegmentTagsFromRespData for v2 API responses. */
// eslint-disable-next-line no-unused-vars
function buildSegmentTags(demographics = {}) {
  const tags = [];
  if (demographics.income) tags.push({ key: 'income', value: demographics.income });
  if (demographics.gender) tags.push({ key: 'gender', value: demographics.gender });
  if (demographics.education) tags.push({ key: 'education', value: demographics.education });
  if (demographics.age) tags.push({ key: 'age', value: demographics.age });
  return tags;
}

/** Formats a raw number into a compact string: 4000000 → "4M", 280000 → "280K". */
function compactNumber(n) {
  if (n >= 1_000_000) return `${+(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return String(n);
}

/** Picks the entry with the highest weight from an array of { label, weight } items. */
function topEntry(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  return arr.reduce((best, cur) => (cur.weight > best.weight ? cur : best), arr[0]);
}

/**
 * Convert a 0–1 weight to a percentage string with smart decimals:
 * whole numbers → "61%", meaningful decimals → "60.5%".
 * Matches the precision used in the Persona Snapshot on the details page.
 */
function weightToPercent(weight) {
  const num = weight * 100;
  const rounded = num % 1 === 0 ? String(Math.round(num)) : num.toFixed(1);
  return `${rounded}%`;
}

/**
 * Builds display tags from the new `respData` / `demographicsV3` / `segmentSize` shape
 * returned by the v2 segments API.
 */
function buildSegmentTagsFromRespData(segment, respData = {}) {
  const tags = [];

  // Size range (e.g. "4M–7M")
  const est = respData.segmentSize?.absolute_estimate;
  if (est) {
    tags.push({ key: 'size', value: `${compactNumber(est.low)}\u2013${compactNumber(est.high)}` });
  }

  const dv3 = respData.demographicsV3 ?? {};

  // Income (dominant bracket, with % when a weight is present)
  const income = topEntry(dv3.income);
  const incomeLabel = income?.label ?? income?.bucket;
  if (incomeLabel) {
    const incomeValue = income.weight != null ? `${incomeLabel}` : incomeLabel;
    tags.push({ key: 'income', value: incomeValue });
  }

  // Location (first entry)
  const loc = dv3.location?.[0]?.label;
  if (loc) tags.push({ key: 'location', value: loc });

  // Gender (dominant with %)
  const gender = topEntry(dv3.gender);
  if (gender?.label) {
    tags.push({ key: 'gender', value: `${gender.label} ${weightToPercent(gender.weight)}` });
  }

  // Ethnicity (dominant with %)
  const eth = topEntry(dv3.ethnicity);
  if (eth?.label) {
    tags.push({ key: 'ethnicity', value: `${eth.label} ${weightToPercent(eth.weight)}` });
  }

  // Education (dominant label)
  const edu = topEntry(dv3.education);
  if (edu?.label) tags.push({ key: 'education', value: edu.label });

  // Age (dominant bucket)
  const age = topEntry(dv3.age);
  if (age?.bucket) tags.push({ key: 'age', value: age.bucket });

  return tags;
}

function buildTagsFromSearchParams(searchParams) {
  const { promoteCategory, promoteCustom, segmentation } = searchParams;
  const tags = [];
  const subjectType = promoteCustom?.trim() || promoteCategory;
  if (subjectType) tags.push({ key: 'type', value: subjectType });

  const demoFieldMap = {
    age: 'age',
    gender: 'gender',
    incomeLevel: 'income',
    education: 'education',
  };
  Object.entries(demoFieldMap).forEach(([key, tagKey]) => {
    const raw = segmentation?.[key];
    const value = Array.isArray(raw) ? raw.join(', ') : raw;
    if (value) tags.push({ key: tagKey, value: String(value) });
  });
  return tags;
}

// ─── Normalisers ──────────────────────────────────────────────────────────────

export function normalizeAudienceSegment(segment) {
  const respData = segment.respData ?? {};

  return {
    // `segmentId` is the positional index used by all API calls (guide §4.9).
    // `id` (DB primary key) is stored separately for reference.
    id: String(segment.segmentId ?? segment.id ?? ''),
    dbId: String(segment.id ?? segment.segmentId ?? ''),
    name: segment.customName ?? segment.name ?? 'Untitled segment',
    description:
      segment.whyThisFits ??
      respData.outline?.description ??
      respData.summary?.textGenerate ??
      segment.description ??
      '',
    thumbnail: segment.imageS3Path ?? segment.imageUrl ?? segment.thumbnail ?? null,
    enrichmentStatus: segment.enrichmentStatus ?? null,
    enrichmentMessage: segment.enrichmentMessage ?? null,
    // Independent axis from enrichmentStatus — a segment can be enrichment-completed while its
    // influencer list is still generating (FE_INFLUENCER_GENERATION_STATUS_GUIDE §2).
    influencersStatus: normalizeInfluencersStatus(segment.influencersStatus),
    selectionStatus: segment.status ?? null,
    archiveStatus: segment.archiveStatus ?? 'Active',
    isArchived: segment.isArchived ?? segment.archiveStatus === 'Archived',
    tags: segment.tags ?? buildSegmentTagsFromRespData(segment, respData),
    respData,
  };
}

/**
 * Maps the `assignedCampaigns` array now returned on each `GET /audiences` row (guide §3) —
 * the campaigns this master has been copied into. Empty array when the master is unassigned.
 */
function normalizeAssignedCampaigns(record) {
  const list = Array.isArray(record.assignedCampaigns) ? record.assignedCampaigns : [];
  return list.map((c) => ({
    campaignId: c.campaignId ?? null,
    campaignName: c.campaignName ?? 'Untitled',
    campaignAudienceId: c.campaignAudienceId ?? null,
    assignedAt: c.assignedAt ?? null,
  }));
}

export function normalizeAudience(record) {
  return {
    id: String(record.id),
    name: record.customLabel ?? 'Untitled',
    url: record.subjectUrl?.replace(/^https?:\/\//, '') ?? null,
    audienceCount: record.segmentCount ?? 0,
    createdAt: record.createdAt ?? null,
    date: formatDate(record.createdAt),
    status: record.status ?? AUDIENCE_STATUS.INITIATED,
    tags: buildTags(record),
    segments: (record.segments ?? []).map(normalizeAudienceSegment),
    assignedCampaigns: normalizeAssignedCampaigns(record),
    generationUsed: record.generationUsed ?? null,
    generationMax: record.generationMax ?? null,
  };
}

export function sortAudiences(audiences, sortBy = 'recent') {
  const list = [...audiences];

  switch (sortBy) {
    case 'name-asc':
      return list.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    case 'name-desc':
      return list.sort((a, b) => b.name.localeCompare(a.name, undefined, { sensitivity: 'base' }));
    case 'segment-count':
      return list.sort((a, b) => (b.audienceCount ?? 0) - (a.audienceCount ?? 0));
    case 'recent':
    default:
      return list.sort((a, b) => {
        const aDay = String(a.createdAt ?? '').slice(0, 10);
        const bDay = String(b.createdAt ?? '').slice(0, 10);
        if (aDay !== bDay) return aDay < bDay ? 1 : -1;
        const aId = Number(a.id);
        const bId = Number(b.id);
        if (Number.isNaN(aId) || Number.isNaN(bId)) return 0;
        return bId - aId;
      });
  }
}

export function normalizeCreatedAudienceCard(createData, searchParams) {
  const audienceId = String(createData.audienceId ?? createData.id);
  const subjectUrl = ensureSubjectUrl(searchParams.url);
  const displayName =
    searchParams.customLabel?.trim() ||
    subjectUrl.replace(/^https?:\/\//, '').split('/')[0] ||
    'Untitled';

  const createdAt = new Date().toISOString();

  return {
    id: audienceId,
    name: displayName,
    url: subjectUrl.replace(/^https?:\/\//, ''),
    audienceCount: 0,
    createdAt,
    date: formatDate(createdAt),
    status: createData.status ?? AUDIENCE_STATUS.INITIATED,
    tags: buildTagsFromSearchParams(searchParams),
    segments: [],
  };
}

// ─── Segment influencers (FE_SEGMENT_INFLUENCERS_GUIDE) ───────────────────────

/** Paid plan generates this many influencers; a trial fills the rest with locked rows. */
export const SEGMENT_INFLUENCERS_PAID_TARGET = 10;

const toSnakeCase = (key) => key.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();

/**
 * The score breakdown comes back either as an object or as a JSON string (the campaign
 * creator rows send the raw column), and the influencer detail endpoint camelCases its keys
 * (`topicScore`) while the list endpoint sends them snake_cased (`topic_score`). The shared
 * score components read snake_case, so every shape is normalised to that. Anything
 * unparseable is treated as "not available yet" so the score falls back to a plain value.
 */
export function parseScoreBreakdown(value) {
  let breakdown = value;
  if (!breakdown) return null;
  if (typeof breakdown === 'string') {
    try {
      breakdown = JSON.parse(breakdown);
    } catch (e) {
      return null;
    }
  }
  if (typeof breakdown !== 'object' || Array.isArray(breakdown)) return null;
  return Object.entries(breakdown).reduce((acc, [key, val]) => {
    acc[toSnakeCase(key)] = val;
    return acc;
  }, {});
}

/**
 * Maps one influencer row (mirrors the campaign `fetchCreators` creator row) to the
 * shape the list table renders. Pre-campaign only `handle` / `platform` / `rationale`
 * are populated — every scoring/metric field stays null until the campaign pipeline fills
 * it, so renderers must be null-safe.
 */
export function normalizeSegmentInfluencer(row, index = 0) {
  const metrics = row?.metrics ?? {};
  const handle = row?.handle ?? '';
  const platform = row?.platform ?? '';
  // The key is the table's React key, so it has to be unique per row. When the backend sends
  // no id, the handle alone is not enough — the same creator can appear on two platforms in
  // one segment, and the collision makes React reuse one row's cells for the other.
  const key = row?.id != null ? String(row.id) : `inf-${platform}-${handle || index}`;
  return {
    id: row?.id != null ? String(row.id) : key,
    key,
    name: row?.fullName ?? handle ?? '',
    username: handle,
    avatar: row?.profilePic ?? null,
    profileUrl: row?.profileUrl ?? null,
    platform: row?.platform ?? null,
    influencerType: row?.influencerType ?? null,
    budget: row?.budget ?? null,
    rationale: row?.rationale ?? '',
    // RAD/Lickly score (0-100) — render as-is, null when the creator has no score.
    score: row?.score ?? null,
    // Per-signal split behind the score ({ topic_score, performance_score, … }) — null
    // until the ML pipeline publishes it, which is why the score cell shows it optionally.
    scoreBreakdown: parseScoreBreakdown(row?.scoreBreakdown ?? row?.score_breakdown),
    // Public links [{ type, url }] — [] when none (FE_SEGMENT_INFLUENCER_NEW_FIELDS).
    affiliationLinks: Array.isArray(row?.affiliationLinks) ? row.affiliationLinks : [],
    // Brand-safety result { safe, flaggedPosts } or null until analysis lands (fills in live
    // on the `segment.brandsafety.ready` event — FE_SEGMENT_INFLUENCER_NEW_FIELDS). Kept because
    // the locked (trial preview) rows still render off it.
    brandSafety: row?.brandSafety ?? null,
    // The per-creator check pair ({ pipelineCheck, userCheck }) the shared Brand Safety badge reads,
    // plus the creator report id every brand-safety endpoint keys off. The BE is adding both to this
    // endpoint as part of AAPU-6775 — until that ships they are null, which the badge renders as the
    // "Run Brand Safety" CTA (disabled without a creatorReportDetailsId).
    brandSafetyCheck: row?.brandSafetyCheck ?? null,
    creatorReportDetailsId: row?.creatorReportDetailsId ?? null,
    // Alias of `platform`: the brand-safety run/availability helpers key on `socialNetwork`, while
    // audience rows spell the same value `platform`.
    socialNetwork: row?.platform ?? null,
    // Audience gender split — already percentages (e.g. 82.12), null when not cached.
    malePct: row?.audienceGenders?.male ?? null,
    femalePct: row?.audienceGenders?.female ?? null,
    followers: metrics.followers ?? null,
    erRate: metrics.engagementRate ?? null,
    avgLikes: metrics.avgLikes ?? null,
    cpe: metrics.cpe ?? null,
    postCount: metrics.postCount ?? null,
    // Campaign-only — not applicable pre-campaign.
    status: null,
    audienceAge: null,
    // Current archive state for this row (FE_ARCHIVE_UNARCHIVE §1). Archived rows are
    // flagged, not removed — they still come back in the list.
    archived: Boolean(row?.archived),
    locked: false,
  };
}

/**
 * Normalises GET …/segments/{id}/influencers into the shape the list renders.
 * A 404 is treated as "unavailable" — existence is not leaked (guide §8).
 */
export function normalizeSegmentInfluencersResponse(resp) {
  // eslint-disable-next-line no-underscore-dangle -- api_client may attach _httpStatus
  const httpStatus = resp?.httpStatus ?? resp?._httpStatus ?? null;

  if (httpStatus === 404) {
    return {
      status: SEGMENT_INFLUENCERS_STATUS.UNAVAILABLE,
      isTrial: false,
      upgradeRequired: false,
      planTarget: 0,
      count: 0,
      influencers: [],
      error: null,
    };
  }

  if (!isApiSuccess(resp) || !resp?.data) {
    return {
      status: null,
      isTrial: false,
      upgradeRequired: false,
      planTarget: 0,
      count: 0,
      influencers: [],
      error: resp?.error ?? resp?.message ?? 'Failed to load influencers.',
    };
  }

  const d = resp.data;
  const rows = Array.isArray(d.influencers) ? d.influencers : [];
  return {
    status: d.status ?? null,
    isTrial: Boolean(d.isTrial),
    upgradeRequired: Boolean(d.upgradeRequired),
    planTarget: d.planTarget ?? 0,
    count: d.count ?? rows.length,
    influencers: rows.map(normalizeSegmentInfluencer),
    error: null,
  };
}

// ─── Create payload ───────────────────────────────────────────────────────────

function formatDemographicValue(value) {
  if (value == null) return null;
  if (Array.isArray(value)) {
    const filtered = value.filter(Boolean);
    return filtered.length > 0 ? filtered.join(', ') : null;
  }
  const str = String(value).trim();
  return str || null;
}

/**
 * Builds POST /api/v1/audiences body from Redux searchParams.
 * Omits optional fields when empty (never auto-fills target).
 *
 * @param {Object} searchParams - Audiences searchParams slice
 * @returns {Object}
 */
export function buildAudienceCreatePayload(searchParams) {
  const { url, promoteCategory, promoteCustom, targetDescription, segmentation } = searchParams;

  const payload = { subjectUrl: ensureSubjectUrl(url) };

  const target = targetDescription?.trim();
  if (target) payload.target = target;

  const subjectType = promoteCustom?.trim() || promoteCategory || null;
  if (subjectType) payload.subjectType = subjectType;

  const demographics = {};
  const demographicFieldMap = {
    age: 'age',
    gender: 'gender',
    incomeLevel: 'income',
    education: 'education',
    location: 'location',
    language: 'language',
  };
  Object.entries(demographicFieldMap).forEach(([segmentationKey, apiKey]) => {
    const formatted = formatDemographicValue(segmentation?.[segmentationKey]);
    if (formatted) demographics[apiKey] = formatted;
  });
  if (Object.keys(demographics).length > 0) {
    payload.demographics = demographics;
  }

  return payload;
}

/** @deprecated Use buildAudienceCreatePayload */
export function buildAudienceSearchPayload(searchParams) {
  return buildAudienceCreatePayload(searchParams);
}

// ─── API response helpers ─────────────────────────────────────────────────────

/**
 * Returns true when the minimum required fields are filled to enable the Search button.
 * @param {Object} searchParams
 * @returns {boolean}
 */
export function isSearchEnabled(searchParams) {
  return Boolean(searchParams.url?.trim());
}

export function isCreateAudienceSuccess(resp) {
  if (!resp) return false;
  if (isApiSuccess(resp)) return true;
  return resp.httpStatus === 201 || resp.status === 201;
}

export function getCreateAudienceErrorMessage(resp) {
  if (!resp) return 'Failed to create audience. Please try again.';

  const validationErrors = resp?.errors;
  const fromFields =
    validationErrors &&
    Object.values(validationErrors)
      .flat()
      .filter(Boolean)
      .join(' ');

  return (
    resp?.error || fromFields || resp?.message || 'Failed to create audience. Please try again.'
  );
}

export function dispatchQuotaExceededIfNeeded(dispatch, resp) {
  const isQuota =
    resp?.feature ||
    resp?.httpStatus === 403 ||
    resp?.status === 403 ||
    resp?.status === 'QUOTA_EXCEEDED';
  if (!isQuota) return;
  dispatch({
    type: SET_QUOTA_EXCEEDED_INFO,
    quotaExceededInfo: {
      errorMessage: resp?.message ?? 'Usage limit reached. Please upgrade your plan.',
      feature: resp?.feature,
    },
  });
}

// ─── Campaign drawer normalisers ──────────────────────────────────────────────

function formatCampaignDrawerDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function normalizeCampaignStatus(status) {
  if (!status) return 'Draft';
  const label = String(status).trim();
  if (!label) return 'Draft';
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/**
 * Maps a /persona/fetchLite record to the shape expected by AudienceCampaignCard.
 */
export function normalizeCampaignForDrawer(record, { segmentId, segmentName } = {}) {
  const segments = Array.isArray(record.statusData) ? record.statusData : [];
  const activeSegments = segments.filter((segment) => segment.status !== 'Reject');
  const status = normalizeCampaignStatus(record.status ?? record.campaignStatus);
  const dateSource =
    record.statusDate ??
    record.updatedAt ??
    record.createdTimestamp ??
    record.createdAt ??
    record.date;

  const alreadyAdded = activeSegments.some((segment, index) => {
    if (segmentId == null) return false;
    const candidates = [
      segment.id,
      segment.segmentId,
      segment.indexId,
      index,
      `${record.builderRequestId}-${index}`,
      `${record.builderRequestId}:${index}`,
    ].filter((value) => value != null && value !== '');

    if (candidates.some((value) => String(value) === String(segmentId))) {
      return true;
    }
    if (segmentName && segment.customName) {
      return segment.customName.trim().toLowerCase() === segmentName.trim().toLowerCase();
    }
    return false;
  });

  const warningFromApi = record.warning ?? record.warningMessage ?? null;
  const warning =
    warningFromApi ??
    (status === 'Active' ? 'Adding audiences may affect active targeting.' : null);

  return {
    id: Number(record.id),
    name: record.campaignName ?? record.name ?? 'Untitled',
    status,
    audienceCount: record.audienceCount ?? record.audience_count ?? activeSegments.length,
    date: formatCampaignDrawerDate(dateSource),
    warning,
    alreadyAdded:
      alreadyAdded || Boolean(record.alreadyAdded ?? record.already_added ?? record.isAdded),
  };
}

/**
 * Normalises the /persona/fetchLite response payload.
 */
export function normalizeFetchLiteForDrawer(data, options = {}) {
  const list = Array.isArray(data) ? data : data?.list ?? [];
  return list
    .filter((record) => record?.campaignName || record?.name)
    .map((record) => normalizeCampaignForDrawer(record, options));
}

/**
 * Normalises a single record from GET /api/v1/audiences/campaigns.
 */
export function normalizeAudiencesCampaign(record) {
  return {
    id: Number(record.id),
    name: record.name ?? 'Untitled',
    status: normalizeCampaignStatus(record.status),
    audienceCount: record.audiencesCount ?? 0,
    date: formatCampaignDrawerDate(record.createdAt),
    warning: null,
    // `alreadyAssigned` is present only when the picker was fetched with ?audienceId=<master>
    // (guide §4); otherwise it is false on every row.
    alreadyAdded: Boolean(record.alreadyAssigned),
  };
}
